export const dashboardEn = {
  dashboard: {
    welcome: "Welcome to Sentinel Crop",
    parcels: "Parcels",
    alerts: "Alerts",
    overview: "Overview",
    totalParcels: "Total Parcels",
    activeAlerts: "Active Alerts",
    totalArea: "Total Area",
    lastUpdate: "Last Update",
    recentActivity: "Recent Activity",
    quickActions: "Quick Actions",
    viewAll: "View All",
    noAlerts: "No active alerts",
    parcelHealth: "Parcel Health",
    weatherForecast: "Weather Forecast",
    upcomingTasks: "Upcoming Tasks"
  },
  emptyState: {
    welcome: "Welcome to Sentinel Crop",
    subtitleParcels: "Start monitoring your parcels with satellite technology and AI-powered insights",
    subtitleGreenSpaces: "Start monitoring your green spaces with satellite technology and AI-powered insights",
    realtimeAnalytics: "Real-time Analytics",
    realtimeAnalyticsDesc: "NDVI, NDWI, EVI indices",
    riskMonitoring: "Risk Monitoring",
    riskMonitoringDesc: "Drought, pest, frost alerts",
    aiRecommendations: "AI Recommendations",
    aiRecommendationsDesc: "Irrigation & fertilization",
    addFirstParcel: "Add Your First Parcel",
    addFirstGreenSpace: "Add Your First Green Space",
    quickStart: "It only takes 2 minutes to get started"
  },
  onboarding: {
    stepOf: "Step {{number}} of 03",
    back: "Back",
    next: "Next",
    skip: "Skip Tour",
    step1Title: "Create Your First Parcel",
    step1Subtitle: "Draw your field on the map",
    step1Description: "Define the boundaries of your agricultural land. We'll automatically calculate the area and start monitoring it with Sentinel-2 satellite imagery.",
    step1Action: "Go to Parcels",
    step1Tip1: "Draw a polygon on the map or enter coordinates",
    step1Tip2: "Add crop type and variety for accurate analysis",
    step1Tip3: "Area is calculated automatically from the polygon",
    step2Title: "View Satellite Analysis",
    step2Subtitle: "Understand your vegetation health",
    step2Description: "Access NDVI vegetation indices from Copernicus Sentinel-2 satellites. See which areas of your field are thriving and which need attention — updated every 5 days.",
    step2Action: "View Satellite Data",
    step2Tip1: "Green = healthy vegetation, Red = stress detected",
    step2Tip2: "Compare time periods to track changes",
    step2Tip3: "Cloud-free imagery automatically selected",
    step3Title: "Get Your First Recommendation",
    step3Subtitle: "AI-powered irrigation optimization",
    step3Description: "Receive personalized irrigation recommendations based on FAO-56 methodology, real-time weather data, and your soil type. Start saving water from day one.",
    step3Action: "Open Irrigation Optimizer",
    step3Tip1: "Recommendations update daily with weather changes",
    step3Tip2: "Based on FAO-56 Penman-Monteith methodology",
    step3Tip3: "Tracks water balance per parcel automatically"
  },
  dashboardPage: {
    welcome: "Farm Operations Center",
    welcomeUrban: "Welcome to Urban Green Management",
    subtitle: "Today's parcel priorities, blocked decisions, data gaps and field actions.",
    subtitleUrban: "Your daily snapshot: green-space health, active alerts and maintenance tasks",
    allSystemsOp: "Operations brief ready",
    opsBriefReady: "Operations brief ready",
    opsBriefReadySub: "Built from current parcel data",
    opsBriefBadge: "Today's brief",
    briefParcelsAttention: "Parcels needing attention",
    briefActiveRisks: "Active risks",
    briefFieldTasks: "Recent field tasks",
    operationalPriorities: "Operational Priorities",
    prioritiesEmptyTitle: "No urgent parcel priorities detected",
    prioritiesEmptyDesc: "Based on current parcel data. New satellite analysis or alerts will appear here automatically.",
    priorityHighRisk: "High satellite risk detected",
    priorityHighRiskReason: "Latest satellite analysis flags this parcel as high risk. Review NDVI and alerts before acting.",
    priorityCriticalRisk: "Critical satellite risk",
    priorityCriticalRiskReason: "Latest satellite analysis marks this parcel as critical. Inspect immediately.",
    priorityMissingAnalysis: "No satellite data yet",
    priorityMissingAnalysisReason: "No satellite analysis recorded for this parcel. Generate analysis to enable decisions.",
    priorityMissingPlanting: "Missing planting date",
    priorityMissingPlantingReason: "Planting date is required to track phenology, irrigation timing and reports.",
    priorityCtaReview: "Review parcel",
    priorityCtaOpen: "Open parcel",
    dataGapsTitle: "Data Gaps Blocking Decisions",
    dataGapsEmpty: "No blocking data gaps detected from the current dashboard data.",
    gapNoAnalytics: "Parcels without satellite analysis",
    gapNoAnalyticsDetail: "These parcels have no NDVI/risk record yet. Decisions cannot be generated.",
    gapNoPlanting: "Parcels missing planting date",
    gapNoPlantingDetail: "Required for phenology, irrigation timing and reports.",
    gapCta: "Manage parcels",
    nextFieldActions: "Next Field Actions",
    portfolioHealth: "Portfolio Health",
    topIssue: "Top issue",
    addNewParcel: "Add New Parcel",
    addNewGreenSpace: "Add New Green Space",
    addFirstParcel: "Add Your First Parcel",
    addFirstGreenSpace: "Add Your First Green Space",
    activeParcels: "Active Parcels",
    activeGreenSpaces: "Active Green Spaces",
    noParcelYet: "No parcels registered yet. Add your first parcel to start monitoring your crops",
    noGreenSpaceYet: "No green spaces registered yet. Start by adding your first green space to monitor.",
    totalCoverage: "Total Coverage",
    activePolicies: "active policies",
    noPoliciesYet: "No policies yet",
    avgNdvi: "Avg. NDVI",
    satelliteAnalysis: "Satellite analysis",
    noDataAvailable: "No data available",
    riskLevel: "Risk Level",
    basedOnSatellite: "Based on satellite data",
    noAssessmentYet: "No assessment yet",
    frostProtection: "Frost Protection",
    frostDesc: "48-72h frost prediction with microclimate modeling and SMS alerts",
    premiumModule: "Premium Module",
    viewDashboard: "View Dashboard →",
    fao56Irrigation: "FAO-56 Irrigation",
    fao56Desc: "Scientific irrigation with Dual Coefficient method (Kcb + Ke)",
    multiStationAemet: "Multi-Station AEMET",
    optimizeNow: "Optimize Now →",
    pkFertilization: "P&K Fertilization",
    pkDesc: "European soil data integration with precision fertilization",
    premium25m: "Premium 25m Data",
    planNutrients: "Plan Nutrients →",
    recentAlerts: "Recent Alerts",
    noAlertsYet: "No Alerts Yet",
    noAlertsDesc: "Once you add parcels, you'll receive alerts about crop conditions and weather events.",
    fieldNotebook: "Field Notebook",
    viewAll: "View All",
    noActivitiesYet: "No Activities Yet",
    noActivitiesDesc: "Start recording your agricultural activities to track your work and get AI validation.",
    cropResources: "Crop Resources & Knowledge Base",
    exploreResources: "Explore Resources",
    cropResourcesDesc: "Access comprehensive guides on crop varieties, planting seasons, growth stages, and agronomic parameters",
    cropProgress: "Crop Progress Tracking",
    addPlantingDates: "Add planting dates to your parcels to track crop progress",
    monitorGrowth: "Monitor growth stages, water needs, and harvest predictions",
    configureParcels: "Configure Parcels",
    marketIntelligence: "Market Intelligence",
    marketDesc: "Real-time commodity pricing and market trends",
    quickActions: "Quick Actions",
    viewSatellite: "View Satellite",
    ndviAnalysis: "NDVI Analysis",
    irrigationLabel: "Irrigation",
    fao56Dual: "FAO-56 Dual",
    pkOptimizer: "P&K Optimizer",
    fertilizerZones: "Fertilizer Zones",
    frostProtectionShort: "Frost Protection",
    alerts48h: "48-72h Alerts",
    ocrFoliar: "OCR Foliar",
    labReports: "Lab Reports",
    phenology: "Phenology",
    growthStages: "Growth Stages",
    analyticsLabel: "Analytics",
    viewInsights: "View Insights",
    viewFullAnalytics: "View Full Analytics",
    loadingDashboard: "Loading your dashboard...",
    accountSetupIncomplete: "Account Setup Incomplete",
    accountSetupDesc: "Your account is not fully configured. Please contact support to complete your setup.",
    backToLogin: "Back to Login",
    sessionExpired: "Session Expired",
    sessionExpiredDesc: "Your session has expired. Please log in again to continue.",
    goToLogin: "Go to Login",
    hectaresTotal: "hectares total",
    notifications: "Notifications",
    notificationsHint: "Items derived from current dashboard data",
    notificationsEmpty: "No pending notifications from current dashboard data.",
    notifType_alert: "Alert",
    notifType_gap: "Data gap",
    notifType_task: "Task",
    notifType_reminder: "Reminder",
    tasksReminders: "Tasks & Reminders",
    tasksRemindersEmpty: "No pending reminders from current dashboard data.",
    reminderConfirmPlanting: "Confirm planting date",
    reminderConfirmPlantingDetail: "Required for phenology, irrigation and reports.",
    reminderGenerateAnalysis: "Generate satellite analysis",
    reminderGenerateAnalysisDetail: "Parcels without NDVI/risk cannot drive decisions.",
    reminderReviewRisk: "Review critical risk signal",
    reminderReviewRiskDetail: "Open the parcel flagged with the highest risk.",
    reminderOpenIrrigation: "Open Irrigation Optimizer",
    reminderOpenIrrigationDetail: "Plan water for parcels with active stress.",
    reminderOpenFarmOps: "Open Gaucho",
    reminderOpenFarmOpsDetail: "Review pending parcel decisions in one place.",
    reminderSelectForAdvisor: "Select parcel for Advisor",
    reminderSelectForAdvisorDetail: "Open a parcel to launch the AI Advisor from its detail view.",
    ctaOpen: "Open",
    ctaReview: "Review",
    olderSignal: "Older signal",
    alertsShownOfTotal: "Showing {{shown}} of {{total}}"
    ,riskSignals: "Risk Signals"
    ,activeSignals: "active signals · older signals available in Alerts"
    ,olderSignalsAvailable: "Older signals available in Alerts"
    ,openAlerts: "Open alerts"
  },
  workflowBanner: {
    title: "Start with Monitor",
    subtitle: "Review parcel signals, alerts and data gaps, then move into irrigation, fertilization, risk or reporting workflows.",
    stepLabels: "Monitor → Decide → Report → Prove"
  },
  reportsCenterBanner: {
    title: "Report & Prove",
    subtitle: "Generate parcel-level reports with data availability, recommendations, limitations and SHA-256 document traceability.",
    quickLinks: {
      digitalParcelRecord: "Digital Parcel Record",
      pacReadiness: "PAC Readiness Check",
      irrigationReports: "Irrigation Reports"
    }
  },
  reportsPurpose: {
    title: "What are these reports for?",
    subtitle: "Reports are designed to support decisions, reviews and internal documentation — not to replace official certification, legal assessment or insurance loss adjustment.",
    cards: {
      explainDecisions: "Explain agronomic decisions",
      documentEvent: "Document insured event conditions",
      internalEvidence: "Prepare internal evidence",
      supportWorkflows: "Support cooperative or advisor workflows"
    }
  },
  sentinelScore: {
    calculating: "Calculating SentinelScore™...",
    excellent: "Excellent",
    good: "Good",
    attention: "Needs Attention",
    critical: "Critical",
    tooltip: "Composite health index: NDVI Trend (30%) + Water Stress (25%) + Frost Risk (20%) + Pest Pressure (15%) + GDD Deviation (10%)",
    byParcel: "Score by Parcel",
    moreParcels: "more parcels",
    noParcels: "Add parcels to see your composite health score."
    ,parcelsTracked: "parcels tracked"
    ,needAttention: "need attention"
    ,viewPortfolio: "View portfolio health"
  }
};
